import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Calendar, DollarSign, Package, CheckCircle, Settings, Users, TrendingUp, TrendingDown, Menu, X, Eye, EyeOff, LogOut, Save, Plus, Edit, Trash2, AlertCircle, FileText, Layers, BarChart3, LineChart, Activity, Upload } from 'lucide-react';
import { useWebSocket } from './hooks/useWebSocket';
import { useTaskPolling } from './hooks/useTaskPolling';
import { useTaskAlarms } from './hooks/useTaskAlarms';
import RealTimeNotifications from './components/RealTimeNotifications';
import { dataService, authService, tasksService, configService, usersService, pendingOrdersService, saldosService } from './services/api';

// Configurar React Query con caché agresivo
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos - datos considerados frescos
      gcTime: 10 * 60 * 1000, // 10 minutos en caché
      refetchOnWindowFocus: false, // No recargar al cambiar de pestaña
      retry: 2,
    },
  },
});

// Context para manejo global de estado
const AppContext = createContext();

// Hook personalizado para usar el contexto - Export para que pueda ser usado por otros componentes
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

// Provider del contexto
const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [showTaskAlarm, setShowTaskAlarm] = useState(false);
  const [newTaskInfo, setNewTaskInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ step: '', percent: 0 });
  
  // === DATOS PRECARGADOS DE MÓDULOS ===
  const [finanzasHistorial, setFinanzasHistorial] = useState([]);
  const [inventoryLocalData, setInventoryLocalData] = useState({});
  const [saldosPendientes, setSaldosPendientes] = useState([]);
  
  // === PRECARGA DE CLIENTES EN SEGUNDO PLANO ===
  const [preloadedCustomers, setPreloadedCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersLastUpdated, setCustomersLastUpdated] = useState(null);

  // === PRECARGA DE PRODUCTOS SIIGO (compartidos entre componentes) ===
  const [siigoProducts, setSiigoProducts] = useState(() => {
    // Restaurar desde sessionStorage para carga instantanea al refrescar
    try {
      const raw = sessionStorage.getItem('siigo_products_v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_) {}
    return [];
  });
  const [siigoProductsLoading, setSiigoProductsLoading] = useState(false);

  const loadSiigoProducts = async (force = false) => {
    if (!force && siigoProducts.length > 100) return siigoProducts;
    if (siigoProductsLoading) return siigoProducts;
    try {
      setSiigoProductsLoading(true);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/siigo/products${force ? '?force_refresh=true' : ''}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success && Array.isArray(data.products)) {
        setSiigoProducts(data.products);
        try {
          sessionStorage.setItem('siigo_products_v1', JSON.stringify(data.products));
        } catch (_) {}
        return data.products;
      }
    } catch (error) {
      console.warn('No se pudieron cargar productos Siigo:', error);
    } finally {
      setSiigoProductsLoading(false);
    }
    return siigoProducts;
  };
  
  // Función para precargar clientes
  const preloadCustomers = async (force = false) => {
    if (!force && preloadedCustomers.length > 100) {
      return preloadedCustomers;
    }
    
    if (customersLoading) {
      return preloadedCustomers;
    }
    
    try {
      setCustomersLoading(true);
      
      // Siempre cargar desde cache primero (rapido). force_refresh solo si el usuario lo pide explicitamente.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/siigo/customers${force ? '?force_refresh=true' : ''}`;
      const response = await fetch(url, { timeout: 15000 });
      const data = await response.json();
      
      if (data.success && data.customers?.length > 0) {
        const consumidorFinal = {
          id: 'consumidor-final',
          identification: '222222222222',
          name: ['CONSUMIDOR', 'FINAL'],
          email: '',
          phone: '',
          address: 'Colombia',
          city: 'Bogota',
          type: 'Customer',
          third_party_type: 'Customer'
        };
        
        const allCustomers = [consumidorFinal, ...data.customers.filter(c => c.identification !== '222222222222')];
        setPreloadedCustomers(allCustomers);
        setCustomersLastUpdated(Date.now());
        
        return allCustomers;
      } else {
        console.warn('⚠️ No se obtuvieron clientes:', data.error || 'Sin datos');
      }
    } catch (error) {
      console.warn('⚠️ Error cargando clientes:', error);
    } finally {
      setCustomersLoading(false);
    }
    return preloadedCustomers;
  };
  
  // Inicializar con datos por defecto mínimos
  const [db, setDb] = useState({
    users: [
      { id: 1, username: 'admin', password: 'admin123', role: 'admin', name: 'Administrador' },
      { id: 2, username: 'invitado', password: 'guest123', role: 'guest', name: 'Usuario Invitado' }
    ],
    accounting: [],
    boskoOrders: [],
    luminaOrders: [],
    pendingOrders: [],
    saldos: [],
    materialesPendientes: [],
    tasks: [],
    scheduledTasks: [],
    colores: [],
    areas: ['Administración', 'Ventas', 'Producción', 'Logística'],
    boskoColors: ['Blanco', 'Negro', 'Madera', 'Gris'],
    paymentMethods: ['Efectivo', 'Tarjeta', 'Transferencia'],
    permissions: {
      admin: ['dashboard', 'accounting', 'cotizador', 'inventario', 'facturacion', 'bosko', 'lumina', 'orders', 'pending-orders', 'gestion-pendientes', 'task-scheduler', 'tasks', 'settings'],
      guest: ['dashboard', 'cotizador', 'inventario', 'facturacion', 'bosko', 'lumina', 'tasks', 'pending-orders', 'gestion-pendientes', 'task-scheduler']
    }
  });

  // Establecer el título de la página
  useEffect(() => {
    document.title = 'CLENIP GROUP - Sistema de Gestión Empresarial';
  }, []);

  // Cargar datos desde el backend al inicializar la aplicación
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        setLoadingProgress({ step: 'Verificando sesion...', percent: 10 });
        
        const savedUser = authService.getCurrentUser();
        if (savedUser && authService.isAuthenticated()) {
          setCurrentUser(savedUser);
          
          setLoadingProgress({ step: 'Cargando datos del sistema...', percent: 30 });
          const backendData = await dataService.bootstrap();
          
          setLoadingProgress({ step: 'Procesando datos...', percent: 70 });
          
          // Separar datos de módulos del estado general
          const { finanzasHistorial: fh, inventoryLocalData: ild, saldosPendientes: sp, ...restData } = backendData;
          setFinanzasHistorial(fh || []);
          setInventoryLocalData(ild || {});
          setSaldosPendientes(sp || []);
          
          setDb(prevDb => ({
            ...prevDb,
            ...restData
          }));
          
          setLoadingProgress({ step: 'Sincronizando clientes...', percent: 85 });
          
          // Precargar clientes en segundo plano
          setTimeout(() => {
            preloadCustomers();
          }, 100);

          // Precargar productos Siigo en segundo plano (para que Visualizador e Inventario
          // se abran instantaneamente sin esperar la descarga)
          setTimeout(() => {
            loadSiigoProducts();
          }, 300);
          
          setLoadingProgress({ step: 'Listo', percent: 100 });
          
        } else {
          console.log('No hay sesion guardada, mostrando login...');
        }
        
      } catch (error) {
        console.error('❌ Error cargando datos iniciales:', error);
        // Si hay error con el token, limpiar la sesión
        if (error.message.includes('401') || error.message.includes('403')) {
          authService.logout();
          setCurrentUser(null);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitialData();
  }, []);

  const value = {
    currentUser,
    setCurrentUser,
    db,
    setDb,
    showTaskAlarm,
    setShowTaskAlarm,
    newTaskInfo,
    setNewTaskInfo,
    isLoading,
    loadingProgress,
    // Datos precargados de módulos
    finanzasHistorial,
    setFinanzasHistorial,
    inventoryLocalData,
    setInventoryLocalData,
    saldosPendientes,
    setSaldosPendientes,
    // Clientes precargados
    preloadedCustomers,
    customersLoading,
    customersLastUpdated,
    preloadCustomers,
    // Productos Siigo precargados (compartidos entre componentes)
    siigoProducts,
    siigoProductsLoading,
    loadSiigoProducts,
    setSiigoProducts
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Componente de Login
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { setCurrentUser, setDb, preloadCustomers, setFinanzasHistorial, setInventoryLocalData, setSaldosPendientes, loadSiigoProducts } = useApp();

  const users = [
    { id: 1, username: 'admin', password: 'admin123', role: 'admin', name: 'Administrador' },
    { id: 2, username: 'invitado', password: 'guest123', role: 'guest', name: 'Usuario Invitado' }
  ];

  const handleLogin = async () => {
    console.log('LOGIN INICIADO');
    
    if (!username || !password) {
      setError('Por favor complete todos los campos');
      return;
    }
    
    try {
      setError('');
      
      // Login usando el servicio API
      const userData = await authService.login({ username, password });
      
      // Establecer usuario en el estado
      setCurrentUser(userData);
      
      // Cargar TODOS los datos con bootstrap (un solo request)
      try {
        const backendData = await dataService.bootstrap();
        
        // Separar datos de módulos
        const { finanzasHistorial: fh, inventoryLocalData: ild, saldosPendientes: sp, ...restData } = backendData;
        setFinanzasHistorial(fh || []);
        setInventoryLocalData(ild || {});
        setSaldosPendientes(sp || []);
        
        setDb(prevDb => ({
          ...prevDb,
          ...restData
        }));
        
        // Precargar clientes en segundo plano
        setTimeout(() => {
          preloadCustomers();
        }, 100);

        // Precargar productos Siigo en segundo plano (compartido entre componentes,
        // persistido en sessionStorage para carga instantanea al refrescar)
        setTimeout(() => {
          loadSiigoProducts && loadSiigoProducts();
        }, 300);
        
      } catch (dataError) {
        console.error('⚠️ Error cargando datos, pero login exitoso:', dataError);
        // No fallar el login si hay error cargando datos
      }
      
    } catch (error) {
      console.error('❌ Error en login:', error);
      setError(error.message || 'Usuario o contraseña incorrectos');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">CLENIP GROUP</h1>
          <p className="text-gray-600">Sistema de Gestión Empresarial</p>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Usuario
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Ingrese su usuario"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="Ingrese su contraseña"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleLogin}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
          >
            Iniciar Sesión
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente Dashboard
const Dashboard = () => {
  const { db } = useApp();
  
  // Asegurarse de que db.accounting existe y es un array
  const accountingRecords = Array.isArray(db.accounting) ? db.accounting : [];
  
  // Debug: Mostrar detalles completos de los registros
  console.log('📊 Dashboard - Total registros contables:', accountingRecords.length);
  
  // Agrupar por caja para ver qué hay en cada una
  const recordsPorCaja = {};
  accountingRecords.forEach(record => {
    const caja = record.box || 'Sin caja';
    if (!recordsPorCaja[caja]) {
      recordsPorCaja[caja] = { ingresos: 0, egresos: 0, count: 0, registros: [] };
    }
    recordsPorCaja[caja].count++;
    recordsPorCaja[caja].registros.push({
      id: record.id,
      date: record.date,
      type: record.type,
      amount: record.amount,
      description: record.description
    });
    if (record.type === 'ingreso') {
      recordsPorCaja[caja].ingresos += parseFloat(record.amount) || 0;
    } else {
      recordsPorCaja[caja].egresos += parseFloat(record.amount) || 0;
    }
  });
  
  console.log('📦 Dashboard - Registros por caja (DETALLADO):');
  Object.keys(recordsPorCaja).forEach(caja => {
    console.log(`\n  🗂️ Caja: ${caja}`);
    console.log(`     📊 Total registros: ${recordsPorCaja[caja].count}`);
    console.log(`     💰 Ingresos: $${recordsPorCaja[caja].ingresos.toLocaleString('es-CO')}`);
    console.log(`     💸 Egresos: $${recordsPorCaja[caja].egresos.toLocaleString('es-CO')}`);
    console.log(`     📝 Registros:`, recordsPorCaja[caja].registros);
  });
  
  // Calcular totales financieros
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  const totalIngresos = accountingRecords
    .filter(r => r.type === 'ingreso')
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    
  const totalEgresos = accountingRecords
    .filter(r => r.type === 'egreso')
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    
  const ingresosDelMes = accountingRecords
    .filter(r => r.type === 'ingreso' && new Date(r.date).getMonth() === currentMonth && new Date(r.date).getFullYear() === currentYear)
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    
  const egresosDelMes = accountingRecords
    .filter(r => r.type === 'egreso' && new Date(r.date).getMonth() === currentMonth && new Date(r.date).getFullYear() === currentYear)
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  
  console.log('\n💰 Dashboard - Total Ingresos calculado:', totalIngresos);
  console.log('💸 Dashboard - Total Egresos calculado:', totalEgresos);
  console.log('📅 Dashboard - Ingresos del mes:', ingresosDelMes);
  console.log('📅 Dashboard - Egresos del mes:', egresosDelMes);
  
  const utilidadDelMes = ingresosDelMes - egresosDelMes;
  const utilidadTotal = totalIngresos - totalEgresos;
  
  // Calcular estadísticas por mes para comparativo
  const mesesNombres = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const ventasPorMes = Array(12).fill(0);
  const ingresosPorMes = Array(12).fill(0);
  const egresosPorMes = Array(12).fill(0);
  
  // Calcular ventas por mes (BOSKO + LUMINA)
  [...db.boskoOrders, ...db.luminaOrders].forEach(order => {
    const mesOrden = new Date(order.date).getMonth();
    ventasPorMes[mesOrden] += order.total || 0;
  });
  
  // Calcular ingresos y egresos por mes usando accountingRecords
  accountingRecords.forEach(record => {
    const mesRecord = new Date(record.date).getMonth();
    const yearRecord = new Date(record.date).getFullYear();
    // Solo contar registros del año actual
    if (yearRecord === currentYear) {
      if (record.type === 'ingreso') {
        ingresosPorMes[mesRecord] += parseFloat(record.amount) || 0;
      } else {
        egresosPorMes[mesRecord] += parseFloat(record.amount) || 0;
      }
    }
  });
  
  // Encontrar el mejor y peor mes
  const mejorMes = ingresosPorMes.indexOf(Math.max(...ingresosPorMes));
  const peorMes = ingresosPorMes.indexOf(Math.min(...ingresosPorMes.filter(v => v > 0)));
  
  const stats = [
    {
      title: 'Total Ingresos',
      value: `$${totalIngresos.toLocaleString('es-CO')}`,
      change: ingresosDelMes > 0 ? `+$${ingresosDelMes.toLocaleString('es-CO')} este mes` : 'Sin movimiento este mes',
      icon: <TrendingUp size={24} />,
      color: 'from-green-500 to-emerald-500',
      changeColor: 'text-green-600'
    },
    {
      title: 'Total Egresos',
      value: `$${totalEgresos.toLocaleString('es-CO')}`,
      change: egresosDelMes > 0 ? `-$${egresosDelMes.toLocaleString('es-CO')} este mes` : 'Sin movimiento este mes',
      icon: <TrendingDown size={24} />,
      color: 'from-red-500 to-pink-500',
      changeColor: 'text-red-600'
    },
    {
      title: 'Utilidad Total',
      value: `$${utilidadTotal.toLocaleString('es-CO')}`,
      change: utilidadDelMes >= 0 ? `+$${utilidadDelMes.toLocaleString('es-CO')} este mes` : `$${utilidadDelMes.toLocaleString('es-CO')} este mes`,
      icon: <DollarSign size={24} />,
      color: utilidadTotal >= 0 ? 'from-blue-500 to-purple-500' : 'from-orange-500 to-red-500',
      changeColor: utilidadDelMes >= 0 ? 'text-green-600' : 'text-red-600'
    },
    {
      title: 'Órdenes Totales',
      value: db.boskoOrders.length + db.luminaOrders.length,
      change: `${db.tasks.filter(t => t.status !== 'completed').length} tareas pendientes`,
      icon: <Package size={24} />,
      color: 'from-indigo-500 to-blue-500',
      changeColor: 'text-blue-600'
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-gray-800">Dashboard Financiero</h2>
        <div className="text-sm text-gray-600">
          Mes actual: {mesesNombres[currentMonth]} {currentYear}
        </div>
      </div>
      
      {/* Cards de resumen financiero */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className={`bg-gradient-to-r ${stat.color} rounded-xl p-6 text-white shadow-lg transform hover:scale-105 transition-all duration-200`}>
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                {stat.icon}
              </div>
            </div>
            <div className="text-2xl font-bold mb-2">{stat.value}</div>
            <div className="text-sm opacity-90 mb-1">{stat.title}</div>
            <div className="text-xs opacity-75">{stat.change}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Comparativo Mensual de Ingresos vs Egresos */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <BarChart3 size={20} className="text-blue-600" />
            Comparativo Mensual - Ingresos vs Egresos
          </h3>
          <div className="space-y-4">
            {mesesNombres.map((mes, index) => {
              const ingresos = ingresosPorMes[index];
              const egresos = egresosPorMes[index];
              const utilidad = ingresos - egresos;
              const maxValue = Math.max(...ingresosPorMes, ...egresosPorMes);
              const ingresosPorcentaje = maxValue > 0 ? (ingresos / maxValue) * 100 : 0;
              const egresosPorcentaje = maxValue > 0 ? (egresos / maxValue) * 100 : 0;
              
              return (
                <div key={index} className={`p-3 rounded-lg ${index === currentMonth ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">{mes}</span>
                    <span className={`text-sm font-medium ${utilidad >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {utilidad >= 0 ? '+' : ''}${utilidad.toLocaleString('es-CO')}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-green-600">Ingresos</span>
                        <span className="font-medium">${ingresos.toLocaleString('es-CO')}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${ingresosPorcentaje}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-red-600">Egresos</span>
                        <span className="font-medium">${egresos.toLocaleString('es-CO')}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${egresosPorcentaje}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Estadísticas de Ventas por Mes */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <LineChart size={20} className="text-purple-600" />
            Evolución de Ventas por Mes
          </h3>
          
          {/* Mejores y peores meses */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm text-green-600 font-medium">Mejor Mes</div>
              <div className="text-xl font-bold text-green-800">{mesesNombres[mejorMes]}</div>
              <div className="text-sm text-green-600">${ingresosPorMes[mejorMes].toLocaleString('es-CO')}</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-sm text-red-600 font-medium">Mes con Menor Actividad</div>
              <div className="text-xl font-bold text-red-800">
                {peorMes !== -1 ? mesesNombres[peorMes] : 'N/A'}
              </div>
              <div className="text-sm text-red-600">
                {peorMes !== -1 ? `$${ingresosPorMes[peorMes].toLocaleString('es-CO')}` : '$0'}
              </div>
            </div>
          </div>

          {/* Gráfico de barras visual para ventas */}
          <div className="space-y-3">
            {mesesNombres.map((mes, index) => {
              const ventas = ventasPorMes[index];
              const maxVentas = Math.max(...ventasPorMes);
              const porcentaje = maxVentas > 0 ? (ventas / maxVentas) * 100 : 0;
              
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-8 text-xs font-medium text-gray-600">{mes}</div>
                  <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full transition-all duration-700 ${
                          index === currentMonth 
                            ? 'bg-gradient-to-r from-purple-500 to-blue-500' 
                            : 'bg-gradient-to-r from-purple-400 to-purple-600'
                        }`}
                        style={{ width: `${porcentaje}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-20 text-xs font-medium text-gray-700">
                    ${ventas.toLocaleString('es-CO')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cuadre por Método de Pago */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <DollarSign size={20} className="text-green-600" />
          Cuadre por Método de Pago
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {db.paymentMethods.map((metodo, index) => {
            const ingresosMetodo = accountingRecords
              .filter(r => r.type === 'ingreso' && (r.paymentMethod === metodo || r.box === metodo))
              .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
            
            const egresosMetodo = accountingRecords
              .filter(r => r.type === 'egreso' && (r.paymentMethod === metodo || r.box === metodo))
              .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
            
            const saldoMetodo = ingresosMetodo - egresosMetodo;
            
            return (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl border border-gray-200 hover:shadow-lg transition-all">
                <div className="text-sm font-medium text-gray-600 mb-3">{metodo}</div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-green-600">Ingresos</span>
                    <span className="text-sm font-semibold text-green-700">
                      +${ingresosMetodo.toLocaleString('es-CO')}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-red-600">Egresos</span>
                    <span className="text-sm font-semibold text-red-700">
                      -${egresosMetodo.toLocaleString('es-CO')}
                    </span>
                  </div>
                </div>
                
                <div className="pt-3 border-t border-gray-300">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Saldo</span>
                    <span className={`text-lg font-bold ${saldoMetodo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                      ${saldoMetodo.toLocaleString('es-CO')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Total General */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border-2 border-blue-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-sm font-medium text-gray-600 mb-2">Total Ingresos</div>
              <div className="text-2xl font-bold text-green-600">
                +${totalIngresos.toLocaleString('es-CO')}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-sm font-medium text-gray-600 mb-2">Total Egresos</div>
              <div className="text-2xl font-bold text-red-600">
                -${totalEgresos.toLocaleString('es-CO')}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-sm font-medium text-gray-600 mb-2">Saldo Final</div>
              <div className={`text-3xl font-bold ${utilidadTotal >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                ${utilidadTotal.toLocaleString('es-CO')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actividad Reciente Mejorada */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <Activity size={20} className="text-indigo-600" />
          Actividad Financiera Reciente
        </h3>
        <div className="space-y-4">
          {db.accounting.slice(-5).reverse().map((record, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  record.type === 'ingreso' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {record.type === 'ingreso' ? (
                    <TrendingUp size={16} className="text-green-600" />
                  ) : (
                    <TrendingDown size={16} className="text-red-600" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-800">{record.description}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Área: {record.area}</span>
                    <span>•</span>
                    <span>{record.paymentMethod}</span>
                    <span>•</span>
                    <span>{new Date(record.date).toLocaleDateString('es-CO')}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-lg font-bold ${
                  record.type === 'ingreso' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {record.type === 'ingreso' ? '+' : '-'}${record.amount.toLocaleString('es-CO')}
                </div>
                <div className={`text-xs px-2 py-1 rounded-full ${
                  record.type === 'ingreso' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {record.type === 'ingreso' ? 'Ingreso' : 'Egreso'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Importar componentes
import Accounting from './components/Accounting';
import CotizadorAutomatico from './components/CotizadorAutomatico';
import Inventario from './components/InventarioNuevo';
import Facturacion from './components/Facturacion';
import Bosko from './components/Bosko';
import LuminaModule from './components/Lumina';
import PendingOrders from './components/PendingOrders';
import ControlPanel from './components/ControlPanel';
import TaskScheduler from './components/TaskScheduler';
import GestionPendientes from './components/GestionPendientes';
import ProductVisualizer from './components/ProductVisualizer';
import MisFacturas from './components/MisFacturas';
import CalendarioBosko from './components/CalendarioBosko';
import MaterialPendiente from './components/MaterialPendiente';
import SaldosPendientes from './components/SaldosPendientes';
import Finanzas from './components/Finanzas';

// Componente Simple de Otros Módulos para funcionalidad básica
const SimpleModule = ({ title, description }) => {
  return (
    <div className="bg-white rounded-xl shadow-md p-8 text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">{title}</h2>
      <p className="text-gray-600 mb-6">{description}</p>
      <div className="bg-blue-50 p-6 rounded-lg">
        <Package size={48} className="text-blue-500 mx-auto mb-4" />
        <p className="text-blue-700">Módulo en desarrollo</p>
      </div>
    </div>
  );
};

const OrdersModule = () => {
  const { db } = useApp();
  const allOrders = [...db.boskoOrders.map(o => ({...o, module: 'BOSKO'})), ...db.luminaOrders.map(o => ({...o, module: 'LUMINA'}))];
  
  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-800 mb-8">Administrador de Órdenes</h2>
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-600">Total Órdenes</h3>
            <p className="text-2xl font-bold text-blue-700">{allOrders.length}</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-600">Pendientes</h3>
            <p className="text-2xl font-bold text-yellow-700">{allOrders.filter(o => o.status === 'pending').length}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-600">En Proceso</h3>
            <p className="text-2xl font-bold text-purple-700">{allOrders.filter(o => o.status === 'in-progress').length}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-600">Completadas</h3>
            <p className="text-2xl font-bold text-green-700">{allOrders.filter(o => o.status === 'completed').length}</p>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-4">ID</th>
                <th className="text-left p-4">Cliente</th>
                <th className="text-left p-4">Módulo</th>
                <th className="text-left p-4">Estado</th>
              </tr>
            </thead>
            <tbody>
              {allOrders.map(order => (
                <tr key={`${order.module}-${order.id}`} className="border-b hover:bg-gray-50">
                  <td className="p-4 font-mono">#{order.id}</td>
                  <td className="p-4">{order.customer}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      order.module === 'BOSKO' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {order.module}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      order.status === 'completed' ? 'bg-green-100 text-green-800' :
                      order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {order.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const TasksModule = () => {
  const { db, setDb } = useApp();
  const [activeTab, setActiveTab] = useState('activas'); // 'activas' o 'completadas'
  const [filterUser, setFilterUser] = useState(() => {
    // Recuperar filtro guardado de localStorage
    return localStorage.getItem('taskUserFilter') || 'todos';
  }); // Filtro por usuario
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Cargar usuarios para el filtro
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const usersList = await usersService.getUsers();
        console.log('📝 TasksModule: Usuarios cargados para filtro:', usersList.length);
        setUsers(usersList);
      } catch (error) {
        console.error('❌ Error cargando usuarios para filtro:', error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);
  
  // Escuchar eventos de nuevas tareas para actualizar la vista
  useEffect(() => {
    const handleTaskCreated = (event) => {
      console.log('📝 TasksModule: Nueva tarea detectada, actualizando vista');
      const { updatedTasks } = event.detail;
      setDb(prevDb => ({
        ...prevDb,
        scheduledTasks: updatedTasks,
        tasks: updatedTasks
      }));
    };

    window.addEventListener('taskCreated', handleTaskCreated);
    return () => window.removeEventListener('taskCreated', handleTaskCreated);
  }, [setDb]);
  
  // Obtener tareas del programador de tareas
  const scheduledTasks = db.scheduledTasks || [];
  
  // Filtrar tareas según la pestaña activa
  const activeTasks = scheduledTasks.filter(t => t.estado === 'programada' || t.estado === 'en-progreso');
  const completedTasks = scheduledTasks.filter(t => t.estado === 'completada');
  
  // Aplicar filtro de usuario a las tareas
  const filterTasksByUser = (tasks) => {
    if (filterUser === 'todos') {
      return tasks;
    }
    return tasks.filter(t => t.assignedTo === filterUser);
  };
  
  const filteredActiveTasks = filterTasksByUser(activeTasks);
  const filteredCompletedTasks = filterTasksByUser(completedTasks);

  const handleAcceptTask = async (task) => {
    try {
      // Usar el ID correcto (puede ser _id del backend o id local)
      const taskId = task._id || task.id;
      console.log('🔄 Aceptando tarea:', taskId, 'Tarea completa:', task);
      
      await tasksService.updateTaskStatus(taskId, 'en-progreso');
      
      // Actualizar estado local
      setDb(prevDb => ({
        ...prevDb,
        scheduledTasks: prevDb.scheduledTasks?.map(t => 
          (t._id === taskId || t.id === taskId) ? { ...t, estado: 'en-progreso' } : t
        ) || [],
        tasks: prevDb.tasks?.map(t => 
          (t._id === taskId || t.id === taskId) ? { ...t, estado: 'en-progreso' } : t
        ) || []
      }));
      
      console.log('✅ Tarea aceptada exitosamente');
    } catch (error) {
      console.error('❌ Error aceptando tarea:', error);
      alert('Error al actualizar la tarea: ' + error.message);
    }
  };

  const handleCompleteTask = async (task) => {
    try {
      // Usar el ID correcto (puede ser _id del backend o id local)
      const taskId = task._id || task.id;
      console.log('🔄 Completando tarea:', taskId, 'Tarea completa:', task);
      
      await tasksService.updateTaskStatus(taskId, 'completada');
      
      // Actualizar estado local
      setDb(prevDb => ({
        ...prevDb,
        scheduledTasks: prevDb.scheduledTasks?.map(t => 
          (t._id === taskId || t.id === taskId) ? { ...t, estado: 'completada' } : t
        ) || [],
        tasks: prevDb.tasks?.map(t => 
          (t._id === taskId || t.id === taskId) ? { ...t, estado: 'completada' } : t
        ) || []
      }));
      
      console.log('✅ Tarea completada exitosamente');
    } catch (error) {
      console.error('❌ Error completando tarea:', error);
      alert('Error al actualizar la tarea: ' + error.message);
    }
  };

  const getStatusColor = (estado) => {
    switch(estado) {
      case 'programada': return 'bg-blue-100 text-blue-800';
      case 'en-progreso': return 'bg-yellow-100 text-yellow-800';
      case 'completada': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (estado) => {
    switch(estado) {
      case 'programada': return 'Programada';
      case 'en-progreso': return 'En Progreso';
      case 'completada': return 'Completada';
      default: return estado;
    }
  };
  
  // Determinar qué tareas mostrar según la pestaña activa
  const tasksToDisplay = activeTab === 'activas' ? filteredActiveTasks : filteredCompletedTasks;
  
  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-800 mb-8">Gestión de Tareas</h2>
      <div className="bg-white rounded-xl shadow-md p-6">
        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-600">Programadas</h3>
            <p className="text-2xl font-bold text-blue-700">{scheduledTasks.filter(t => t.estado === 'programada').length}</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-600">En Progreso</h3>
            <p className="text-2xl font-bold text-yellow-700">{scheduledTasks.filter(t => t.estado === 'en-progreso').length}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-600">Completadas</h3>
            <p className="text-2xl font-bold text-green-700">{scheduledTasks.filter(t => t.estado === 'completada').length}</p>
          </div>
        </div>
        
        {/* Pestañas */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('activas')}
            className={`px-6 py-3 font-semibold transition-all relative ${
              activeTab === 'activas'
                ? 'text-red-600 border-b-2 border-red-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Tareas Activas
            <span className={`ml-2 px-2 py-1 rounded-full text-xs font-bold ${
              activeTab === 'activas' 
                ? 'bg-red-100 text-red-600' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {filteredActiveTasks.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('completadas')}
            className={`px-6 py-3 font-semibold transition-all relative ${
              activeTab === 'completadas'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Tareas Completadas
            <span className={`ml-2 px-2 py-1 rounded-full text-xs font-bold ${
              activeTab === 'completadas' 
                ? 'bg-green-100 text-green-600' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {filteredCompletedTasks.length}
            </span>
          </button>
        </div>
        
        {/* Filtro por Usuario */}
        <div className="mb-4 flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">
            Filtrar por Usuario:
          </label>
          <select
            value={filterUser}
            onChange={(e) => {
              console.log('🔍 Filtro de usuario cambiado a:', e.target.value);
              setFilterUser(e.target.value);
              localStorage.setItem('taskUserFilter', e.target.value);
            }}
            className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all min-w-[200px]"
            disabled={loadingUsers}
          >
            <option value="todos">
              {loadingUsers ? 'Cargando...' : 'Todos los usuarios'}
            </option>
            {users.map(user => (
              <option key={user._id || user.id} value={user.username}>
                {user.name} ({user.username})
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">
            Mostrando {activeTab === 'activas' ? filteredActiveTasks.length : filteredCompletedTasks.length} de {activeTab === 'activas' ? activeTasks.length : completedTasks.length} tareas
          </span>
        </div>
        
        {/* Lista de Tareas */}
        <div className="space-y-4">
          {tasksToDisplay.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-lg mb-2">
                {activeTab === 'activas' 
                  ? 'No hay tareas activas' 
                  : 'No hay tareas completadas'}
              </p>
              <p className="text-sm">
                {activeTab === 'activas'
                  ? 'Las tareas creadas en "Programador de Tareas" aparecerán aquí'
                  : 'Las tareas completadas se mostrarán en esta sección'}
              </p>
            </div>
          ) : (
            tasksToDisplay.map(task => (
              <div key={task.id} className="bg-gray-50 rounded-lg p-5 border hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-800">{task.nombre}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(task.estado)}`}>
                        {getStatusText(task.estado)}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Bodega:</span> {task.bodega}
                    </div>
                    
                    {task.createdBy && (
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Programado por:</span>{' '}
                        <span className="text-purple-600 font-medium">{task.createdBy}</span>
                      </div>
                    )}
                    
                    <p className="text-gray-700 bg-white p-3 rounded-lg border mb-3">
                      {task.descripcion}
                    </p>
                    
                    <div className="text-xs text-gray-500">
                      Creado: {new Date(task.fechaCreacion).toLocaleDateString('es-CO', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  
                  {/* Botones de acción solo para tareas activas */}
                  {activeTab === 'activas' && (
                    <div className="flex flex-col gap-2 ml-4">
                      {task.estado === 'programada' && (
                        <button
                          onClick={() => handleAcceptTask(task)}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                        >
                          <CheckCircle size={16} />
                          Aceptar
                        </button>
                      )}
                      
                      {task.estado === 'en-progreso' && (
                        <button
                          onClick={() => handleCompleteTask(task)}
                          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                        >
                          <CheckCircle size={16} />
                          Hecho
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Para tareas completadas, solo mostrar el indicador */}
                  {activeTab === 'completadas' && (
                    <div className="text-green-600 font-medium text-sm flex items-center gap-2 ml-4">
                      <CheckCircle size={20} />
                      Completada
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const SettingsModule = () => {
  const { currentUser, db, setDb } = useApp();
  const [activeTab, setActiveTab] = useState('users');
  
  // Estados para modales
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [showCajaModal, setShowCajaModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editingArea, setEditingArea] = useState(null);
  const [editingCaja, setEditingCaja] = useState(null);
  
  // Estados para formularios
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    name: '',
    role: 'user', // Rol genérico, ya no importa para permisos
    customPermissions: []
  });
  
  const [showPassword, setShowPassword] = useState(false); // Estado para mostrar/ocultar contraseña
  
  const [paymentForm, setPaymentForm] = useState('');
  const [areaForm, setAreaForm] = useState('');
  const [cajaForm, setCajaForm] = useState('');
  const [colorForm, setColorForm] = useState('');
  const [showColorModal, setShowColorModal] = useState(false);

  // Verificar permisos de administrador
  const user = currentUser.user || currentUser;
  const isAdmin = user.username === 'admin' || user.username === 'Leandro' || user.role === 'admin';
  
  if (!isAdmin) {
    return (
      <div className="bg-white rounded-xl shadow-md p-8 text-center">
        <AlertCircle size={48} className="text-yellow-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">Acceso Restringido</h3>
        <p className="text-gray-600">Solo los administradores pueden acceder a la configuración del sistema.</p>
        <p className="text-sm text-gray-500 mt-2">Usuario actual: {user.username} (Role: {user.role})</p>
      </div>
    );
  }

  // Opciones de módulos disponibles
  const availableModules = [
    { id: 'dashboard', name: 'Dashboard', icon: '📊' },
    { id: 'accounting', name: 'Contabilidad', icon: '💰' },
    { id: 'finanzas', name: 'Finanzas', icon: '📈' },
    { id: 'cotizador', name: 'Cotizador automatico', icon: '🧮' },
    { id: 'inventario', name: 'Inventario', icon: '📦' },
    { id: 'facturacion', name: 'Facturación', icon: '🧾' },
    { id: 'mis-facturas', name: 'Mis Facturas', icon: '📄' },
    { id: 'bosko', name: 'BOSKO', icon: '🛋️' },
    { id: 'lumina', name: 'LUMINA', icon: '🪟' },
    { id: 'orders', name: 'Panel de Control', icon: '📋' },
    { id: 'pending-orders', name: 'Pedidos Pendientes', icon: '⏰' },
    { id: 'gestion-pendientes', name: 'Gestión de Pendientes', icon: '📦' },
    { id: 'task-scheduler', name: 'Programador de Tareas', icon: '📅' },
    { id: 'calendario-bosko', name: 'Calendario Bosko', icon: '🗓️' },
    { id: 'tasks', name: 'Tareas', icon: '✅' },
    { id: 'settings', name: 'Configuración', icon: '⚙️' },
    { id: 'product-visualizer', name: 'Visualizador de Productos', icon: '👁️' }
  ];

  // Funciones para usuarios
  const handleCreateUser = async () => {
    if (!userForm.username || !userForm.password || !userForm.name) {
      alert('Por favor complete todos los campos');
      return;
    }

    try {
      const userData = {
        username: userForm.username,
        password: userForm.password,
        name: userForm.name,
        role: userForm.role,
        customPermissions: userForm.customPermissions
      };

      console.log('🔄 Creando usuario en el backend...', userData);
      
      // Crear usuario usando el endpoint de registro del backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(currentUser.access_token || 'temp_token')}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${await response.text()}`);
      }

      const newUser = await response.json();
      console.log('✅ Usuario creado exitosamente:', newUser);

      // Actualizar estado local
      setDb(prevDb => ({
        ...prevDb,
        users: [...prevDb.users, newUser],
        permissions: {
          ...prevDb.permissions,
          [newUser.username]: userForm.customPermissions
        }
      }));

      setUserForm({ username: '', password: '', name: '', role: 'user', customPermissions: [] });
      setShowPassword(false); // Resetear el toggle de contraseña
      setShowUserModal(false);
      alert('✅ Usuario creado y guardado exitosamente');

    } catch (error) {
      console.error('❌ Error creando usuario:', error);
      alert('❌ Error al crear usuario: ' + error.message);
    }
  };

  const handleEditUser = (user) => {
    console.log('🔍 Editando usuario:', user);
    setEditingUser(user);
    setUserForm({
      username: user.username,
      password: '', // No mostrar la contraseña hasheada, dejar vacío
      name: user.name,
      role: user.role,
      customPermissions: user.customPermissions || [] // Cargar desde el usuario directamente
    });
    setShowUserModal(true);
  };

  const handleUpdateUser = async () => {
    if (!userForm.username || !userForm.name) {
      alert('Por favor complete el nombre de usuario y nombre completo');
      return;
    }

    try {
      const userId = editingUser._id || editingUser.id;
      const userData = {
        username: userForm.username,
        password: userForm.password, // Puede estar vacío si no se quiere cambiar
        name: userForm.name,
        role: userForm.role,
        customPermissions: userForm.customPermissions
      };

      console.log('🔄 Actualizando usuario en el backend...', userId, userData);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(currentUser.access_token || 'temp_token')}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error ${response.status}`);
      }

      const updatedUser = await response.json();
      console.log('✅ Usuario actualizado exitosamente:', updatedUser);

      // Actualizar estado local
      setDb(prevDb => ({
        ...prevDb,
        users: prevDb.users.map(user => 
          (user._id || user.id) === userId 
            ? { ...user, ...updatedUser }
            : user
        ),
        permissions: {
          ...prevDb.permissions,
          [userForm.username]: userForm.customPermissions
        }
      }));

      setEditingUser(null);
      setUserForm({ username: '', password: '', name: '', role: 'user', customPermissions: [] });
      setShowPassword(false); // Resetear el toggle de contraseña
      setShowUserModal(false);
      alert('✅ Usuario actualizado exitosamente');

    } catch (error) {
      console.error('❌ Error actualizando usuario:', error);
      alert('❌ Error al actualizar usuario: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('¿Está seguro de eliminar este usuario?')) {
      try {
        console.log('🔄 Eliminando usuario con ID:', userId);
        console.log('🔍 Tipo de ID:', typeof userId, 'Valor:', userId);
        await configService.deleteUser(userId);
        
        // Actualizar estado local
        setDb(prevDb => ({
          ...prevDb,
          users: prevDb.users.filter(user => (user._id || user.id) !== userId)
        }));
        
        alert('✅ Usuario eliminado exitosamente');
        console.log('✅ Usuario eliminado exitosamente');
        
      } catch (error) {
        console.error('❌ Error eliminando usuario:', error);
        alert('❌ Error al eliminar usuario: ' + error.message);
      }
    }
  };

  const handlePermissionToggle = (moduleId) => {
    setUserForm(prev => ({
      ...prev,
      customPermissions: prev.customPermissions.includes(moduleId)
        ? prev.customPermissions.filter(id => id !== moduleId)
        : [...prev.customPermissions, moduleId]
    }));
  };

  // Funciones para métodos de pago
  const handleCreatePayment = async () => {
    if (!paymentForm.trim()) {
      alert('Por favor ingrese el método de pago');
      return;
    }

    try {
      console.log('🔄 Creando método de pago...', paymentForm);
      await configService.createPaymentMethod(paymentForm);
      
      // Actualizar estado local
      setDb(prevDb => ({
        ...prevDb,
        paymentMethods: [...(prevDb.paymentMethods || []), paymentForm]
      }));

      // Emitir evento para actualizar otros componentes
      window.dispatchEvent(new CustomEvent('configUpdated', {
        detail: { type: 'paymentMethod', action: 'create', method: paymentForm }
      }));

      setPaymentForm('');
      setShowPaymentModal(false);
      alert('✅ Método de pago guardado exitosamente');
      console.log('✅ Método de pago creado exitosamente');

    } catch (error) {
      console.error('❌ Error creando método de pago:', error);
      alert('❌ Error al crear método de pago: ' + error.message);
    }
  };

  const handleDeletePayment = async (method) => {
    if (window.confirm('¿Está seguro de eliminar este método de pago?')) {
      try {
        console.log('🔄 Eliminando método de pago...', method);
        await configService.deletePaymentMethod(method);
        
        // Actualizar estado local
        setDb(prevDb => ({
          ...prevDb,
          paymentMethods: prevDb.paymentMethods.filter(p => p !== method)
        }));
        
        // Emitir evento para actualizar otros componentes
        window.dispatchEvent(new CustomEvent('configUpdated', {
          detail: { type: 'paymentMethod', action: 'delete', method }
        }));
        
        alert('✅ Método de pago eliminado exitosamente');
        console.log('✅ Método de pago eliminado exitosamente');
        
      } catch (error) {
        console.error('❌ Error eliminando método de pago:', error);
        alert('❌ Error al eliminar método de pago: ' + error.message);
      }
    }
  };

  // Funciones para áreas
  const handleCreateArea = async () => {
    if (!areaForm.trim()) {
      alert('Por favor ingrese el nombre del área');
      return;
    }

    try {
      console.log('🔄 Creando área...', areaForm);
      await configService.createArea(areaForm);
      
      // Actualizar estado local
      setDb(prevDb => ({
        ...prevDb,
        areas: [...(prevDb.areas || []), areaForm]
      }));

      setAreaForm('');
      setShowAreaModal(false);
      alert('✅ Área guardada exitosamente');
      console.log('✅ Área creada exitosamente');

    } catch (error) {
      console.error('❌ Error creando área:', error);
      alert('❌ Error al crear área: ' + error.message);
    }
  };

  const handleDeleteArea = async (area) => {
    if (window.confirm('¿Está seguro de eliminar esta área?')) {
      try {
        console.log('🔄 Eliminando área...', area);
        await configService.deleteArea(area);
        
        // Actualizar estado local
        setDb(prevDb => ({
          ...prevDb,
          areas: prevDb.areas.filter(a => a !== area)
        }));
        
        alert('✅ Área eliminada exitosamente');
        console.log('✅ Área eliminada exitosamente');
        
      } catch (error) {
        console.error('❌ Error eliminando área:', error);
        alert('❌ Error al eliminar área: ' + error.message);
      }
    }
  };

  // Funciones para cajas
  const handleCreateCaja = async () => {
    if (!cajaForm.trim()) {
      alert('Por favor ingrese el nombre de la caja');
      return;
    }

    try {
      console.log('🔄 Creando caja...', cajaForm);
      await configService.createCaja(cajaForm);
      
      // Actualizar estado local
      setDb(prevDb => ({
        ...prevDb,
        cajas: [...(prevDb.cajas || []), cajaForm]
      }));

      setCajaForm('');
      setShowCajaModal(false);
      alert('✅ Caja guardada exitosamente');
      console.log('✅ Caja creada exitosamente');

    } catch (error) {
      console.error('❌ Error creando caja:', error);
      alert('❌ Error al crear caja: ' + error.message);
    }
  };

  const handleDeleteCaja = async (caja) => {
    if (window.confirm('¿Está seguro de eliminar esta caja?')) {
      try {
        console.log('🔄 Eliminando caja...', caja);
        await configService.deleteCaja(caja);
        
        // Actualizar estado local
        setDb(prevDb => ({
          ...prevDb,
          cajas: prevDb.cajas.filter(c => c !== caja)
        }));
        
        alert('✅ Caja eliminada exitosamente');
        console.log('✅ Caja eliminada exitosamente');
        
      } catch (error) {
        console.error('❌ Error eliminando caja:', error);
        alert('❌ Error al eliminar caja: ' + error.message);
      }
    }
  };

  // Funciones para colores
  const createColor = async () => {
    if (!colorForm.trim()) {
      alert('Por favor ingrese el nombre del color');
      return;
    }

    try {
      await configService.createColor(colorForm);
      
      setDb(prevDb => ({
        ...prevDb,
        colores: [...(prevDb.colores || []), colorForm]
      }));

      window.dispatchEvent(new CustomEvent('configUpdated', {
        detail: { type: 'color', action: 'create', color: colorForm }
      }));

      setColorForm('');
      setShowColorModal(false);
      alert('✅ Color guardado exitosamente');
    } catch (error) {
      console.error('❌ Error al crear color:', error);
      alert('❌ Error al crear el color: ' + error.message);
    }
  };

  const deleteColor = async (color) => {
    if (!confirm(`¿Estás seguro de que quieres eliminar el color "${color}"?`)) return;
    
    try {
      await configService.deleteColor(color);
      
      setDb(prevDb => ({
        ...prevDb,
        colores: prevDb.colores.filter(c => c !== color)
      }));
      
      window.dispatchEvent(new CustomEvent('configUpdated', {
        detail: { type: 'color', action: 'delete', color }
      }));
      
      alert('✅ Color eliminado exitosamente');
    } catch (error) {
      console.error('❌ Error eliminando color:', error);
      alert('❌ Error al eliminar el color: ' + error.message);
    }
  };

  const tabs = [
    { id: 'users', name: 'Usuarios', icon: '👤' },
    { id: 'payments', name: 'Métodos de Pago', icon: '💳' },
    { id: 'areas', name: 'Áreas', icon: '🏢' },
    { id: 'cajas', name: 'Cajas', icon: '💰' },
    { id: 'colores', name: 'Colores', icon: '🎨' },
    { id: 'limpieza', name: 'Limpieza de Datos', icon: '🧹' }
  ];
  
  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-800 mb-8">Configuración del Sistema</h2>
      
      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-lg">{tab.icon}</span>
                  {tab.name}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Contenido por tab */}
      <div className="bg-white rounded-xl shadow-md p-6">
        
        {/* TAB USUARIOS */}
        {activeTab === 'users' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold">Gestión de Usuarios</h3>
              <button
                onClick={() => {
                  setEditingUser(null);
                  setUserForm({ username: '', password: '', name: '', role: 'user', customPermissions: [] });
                  setShowUserModal(true);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} />
                Crear Usuario
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-4">Usuario</th>
                    <th className="text-left p-4">Nombre</th>
                    <th className="text-left p-4">Rol</th>
                    <th className="text-left p-4">Permisos</th>
                    <th className="text-center p-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {db.users.map(user => (
                    <tr key={user.id} className="border-b hover:bg-gray-50">
                      <td className="p-4 font-medium">{user.username}</td>
                      <td className="p-4">{user.name}</td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-600">
                          {(user.customPermissions || db.permissions[user.username] || []).length} módulos
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <Edit size={18} />
                          </button>
                          {user.username !== 'admin' && (
                            <button
                              onClick={() => handleDeleteUser(user._id || user.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB MÉTODOS DE PAGO */}
        {activeTab === 'payments' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold">Métodos de Pago</h3>
              <button
                onClick={() => {
                  setPaymentForm('');
                  setShowPaymentModal(true);
                }}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} />
                Agregar Método
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(db.paymentMethods || []).map((method, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                  <span className="font-medium">{method}</span>
                  <button
                    onClick={() => handleDeletePayment(method)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB ÁREAS */}
        {activeTab === 'areas' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold">Áreas de la Empresa</h3>
              <button
                onClick={() => {
                  setAreaForm('');
                  setShowAreaModal(true);
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} />
                Agregar Área
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(db.areas || []).map((area, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                  <span className="font-medium">{area}</span>
                  <button
                    onClick={() => handleDeleteArea(area)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB CAJAS */}
        {activeTab === 'cajas' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold">Gestión de Cajas</h3>
              <button
                onClick={() => {
                  setCajaForm('');
                  setShowCajaModal(true);
                }}
                className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} />
                Agregar Caja
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(db.cajas || ['Caja Menor', 'Caja Mayor']).map((caja, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                  <span className="font-medium">{caja}</span>
                  <button
                    onClick={() => handleDeleteCaja(caja)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB COLORES */}
        {activeTab === 'colores' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold">Gestión de Colores</h3>
              <button
                onClick={() => {
                  setColorForm('');
                  setShowColorModal(true);
                }}
                className="bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} />
                Agregar Color
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(db.colores || ['Blanco', 'Negro', 'Madera', 'Gris']).map((color, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                  <span className="font-medium">{color}</span>
                  <button
                    onClick={() => deleteColor(color)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB LIMPIEZA DE DATOS */}
        {activeTab === 'limpieza' && (
          <div>
            <div className="mb-6">
              <h3 className="text-2xl font-semibold mb-2">Limpieza de Datos de Contabilidad</h3>
              <p className="text-gray-600">Elimina cajas antiguas o registros fantasma que ya no necesitas</p>
            </div>

            <div className="space-y-4">
              {(() => {
                // Agrupar registros por caja
                const recordsPorCaja = {};
                const accountingRecords = Array.isArray(db.accounting) ? db.accounting : [];
                
                accountingRecords.forEach(record => {
                  const caja = record.box || 'Sin caja';
                  if (!recordsPorCaja[caja]) {
                    recordsPorCaja[caja] = { ingresos: 0, egresos: 0, count: 0, ids: [] };
                  }
                  recordsPorCaja[caja].count++;
                  recordsPorCaja[caja].ids.push(record.id);
                  if (record.type === 'ingreso') {
                    recordsPorCaja[caja].ingresos += parseFloat(record.amount) || 0;
                  } else {
                    recordsPorCaja[caja].egresos += parseFloat(record.amount) || 0;
                  }
                });

                return Object.keys(recordsPorCaja).map((caja) => {
                  const data = recordsPorCaja[caja];
                  const saldo = data.ingresos - data.egresos;
                  
                  return (
                    <div key={caja} className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl border-2 border-gray-200">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-800 mb-2">📦 {caja}</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Registros:</span>
                              <span className="ml-2 font-semibold text-blue-600">{data.count}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Ingresos:</span>
                              <span className="ml-2 font-semibold text-green-600">
                                ${data.ingresos.toLocaleString('es-CO')}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Egresos:</span>
                              <span className="ml-2 font-semibold text-red-600">
                                ${data.egresos.toLocaleString('es-CO')}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Saldo:</span>
                              <span className={`ml-2 font-semibold ${saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                                ${saldo.toLocaleString('es-CO')}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={async () => {
                            if (window.confirm(`¿Estás seguro de eliminar TODOS los registros de la caja "${caja}"?\n\nSe eliminarán ${data.count} registros permanentemente.`)) {
                              try {
                                // Usar el nuevo endpoint de limpieza masiva
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cleanup-box/${encodeURIComponent(caja)}`, {
                                  method: 'DELETE',
                                  headers: {
                                    'Authorization': `Bearer ${currentUser.access_token || 'temp_token'}`,
                                    'Content-Type': 'application/json'
                                  }
                                });
                                
                                if (!response.ok) {
                                  throw new Error('Error al eliminar registros del backend');
                                }
                                
                                const result = await response.json();
                                console.log('✅ Registros eliminados del backend:', result);
                                
                                // Actualizar estado local
                                setDb(prevDb => ({
                                  ...prevDb,
                                  accounting: prevDb.accounting.filter(r => r.box !== caja)
                                }));
                                
                                alert(`✅ Se eliminaron ${result.deleted_count} registros de la caja "${caja}" del backend`);
                                
                                // Recargar los datos para asegurar sincronización
                                setTimeout(() => {
                                  window.location.reload();
                                }, 1000);
                              } catch (error) {
                                console.error('Error eliminando registros:', error);
                                alert('❌ Error al eliminar los registros: ' + error.message);
                              }
                            }
                          }}
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
                        >
                          <Trash2 size={18} />
                          Eliminar Caja
                        </button>
                      </div>
                      
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-3 text-sm text-yellow-800">
                        ⚠️ <strong>Advertencia:</strong> Esta acción eliminará permanentemente todos los registros de esta caja del backend y no se puede deshacer.
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {(() => {
              const accountingRecords = Array.isArray(db.accounting) ? db.accounting : [];
              const recordsPorCaja = {};
              accountingRecords.forEach(record => {
                const caja = record.box || 'Sin caja';
                if (!recordsPorCaja[caja]) recordsPorCaja[caja] = 0;
                recordsPorCaja[caja]++;
              });
              
              return Object.keys(recordsPorCaja).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>No hay registros de contabilidad para limpiar</p>
                </div>
              ) : null;
            })()}
          </div>
        )}
      </div>

      {/* MODAL USUARIO */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">
                {editingUser ? 'Editar Usuario' : 'Crear Usuario'}
              </h3>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usuario *
                  </label>
                  <input
                    type="text"
                    value={userForm.username}
                    onChange={(e) => setUserForm(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="nombre_usuario"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contraseña *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={userForm.password}
                      onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Contraseña segura"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Nombre completo del usuario"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Permisos de Acceso
                  <p className="text-sm text-gray-500 mt-1">Selecciona los módulos a los que este usuario puede acceder</p>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {availableModules.map(module => (
                    <label key={module.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={userForm.customPermissions.includes(module.id)}
                        onChange={() => handlePermissionToggle(module.id)}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-lg">{module.icon}</span>
                      <span className="font-medium">{module.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={editingUser ? handleUpdateUser : handleCreateUser}
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white py-3 rounded-lg font-medium"
                >
                  {editingUser ? 'Actualizar Usuario' : 'Crear Usuario'}
                </button>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MÉTODO DE PAGO */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Agregar Método de Pago</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Método de Pago *
                </label>
                <input
                  type="text"
                  value={paymentForm}
                  onChange={(e) => setPaymentForm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ej: Efectivo, Tarjeta de Crédito, PayPal..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleCreatePayment}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg font-medium"
                >
                  Agregar
                </button>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ÁREA */}
      {showAreaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Agregar Área</h3>
              <button
                onClick={() => setShowAreaModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Área *
                </label>
                <input
                  type="text"
                  value={areaForm}
                  onChange={(e) => setAreaForm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Ej: Ventas, Producción, Recursos Humanos..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleCreateArea}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg font-medium"
                >
                  Agregar
                </button>
                <button
                  onClick={() => setShowAreaModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CAJA */}
      {showCajaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Agregar Caja</h3>
              <button
                onClick={() => setShowCajaModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la Caja *
                </label>
                <input
                  type="text"
                  value={cajaForm}
                  onChange={(e) => setCajaForm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Ej: Caja Chica, Caja Principal, Caja de Emergencia..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleCreateCaja}
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white py-2 rounded-lg font-medium"
                >
                  Agregar
                </button>
                <button
                  onClick={() => setShowCajaModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL COLOR */}
      {showColorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Agregar Color</h3>
              <button
                onClick={() => setShowColorModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Color *
                </label>
                <input
                  type="text"
                  value={colorForm}
                  onChange={(e) => setColorForm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Ej: Azul, Rojo, Verde, Amarillo..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={createColor}
                  className="flex-1 bg-pink-500 hover:bg-pink-600 text-white py-2 rounded-lg font-medium"
                >
                  Agregar
                </button>
                <button
                  onClick={() => setShowColorModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg font-medium"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente de Alarma para Nueva Tarea - VERSION MEJORADA
const TaskAlarmWebSocket = ({ showTaskAlarm, alarmData, closeTaskAlarm }) => {
  const [timeLeft, setTimeLeft] = React.useState(10);
  const countdownRef = React.useRef(null);
  
  // Reproducir sonido beep y auto-cerrar después de 10 segundos
  React.useEffect(() => {
    if (showTaskAlarm && alarmData) {
      console.log('🚨 Alarma mostrada, iniciando countdown...');
      
      // Crear y reproducir sonido beep
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const playBeep = () => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = 800;
          oscillator.type = 'square';
          
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.5);
        };
        
        // Reproducir beep 3 veces
        playBeep();
        setTimeout(playBeep, 300);
        setTimeout(playBeep, 600);
        
        // Cerrar audio context después
        setTimeout(() => {
          if (audioContext.state !== 'closed') {
            audioContext.close();
          }
        }, 1000);
      } catch (e) {
        console.error('Error reproduciendo sonido:', e);
      }
      
      // Reiniciar contador
      setTimeLeft(10);
      
      // Limpiar intervalo anterior si existe
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      
      // Contador regresivo de 10 segundos
      countdownRef.current = setInterval(() => {
        setTimeLeft(prev => {
          const newValue = prev - 1;
          console.log('⏱️ Countdown:', newValue);
          
          if (newValue <= 0) {
            console.log('⏰ Tiempo agotado, cerrando alarma...');
            clearInterval(countdownRef.current);
            closeTaskAlarm();
            return 0;
          }
          return newValue;
        });
      }, 1000);
      
      return () => {
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
      };
    }
  }, [showTaskAlarm, alarmData]);
  
  // Handler para cerrar manualmente
  const handleClose = () => {
    console.log('🔘 Botón ENTENDIDO presionado');
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    closeTaskAlarm();
  };
  
  if (!showTaskAlarm || !alarmData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
      <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-xl p-6 max-w-xl w-full shadow-2xl border-4 border-yellow-400 max-h-[90vh] overflow-y-auto">
        <div className="text-center relative">
          {/* Contador regresivo */}
          <div className="absolute top-0 right-0 bg-yellow-400 text-red-800 font-black text-xl px-3 py-1 rounded-full shadow-lg">
            {timeLeft}s
          </div>
          
          {/* Icono de alarma */}
          <div className="mb-4 mt-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400 rounded-full">
              <AlertCircle size={32} className="text-red-600" />
            </div>
          </div>

          {/* Título principal */}
          <h1 className="text-4xl font-black text-white mb-3 tracking-wider">
            🚨 NUEVA TAREA 🚨
          </h1>
          
          {/* Información de la tarea */}
          <div className="bg-white rounded-lg p-4 mb-4 text-left">
            <h3 className="text-xl font-bold text-red-600 mb-3">Detalles de la Tarea:</h3>
            <div className="space-y-2">
              <div>
                <span className="font-bold text-gray-800 text-sm">📝 Nombre:</span>
                <p className="text-base text-gray-700 ml-4">{alarmData.nombre}</p>
              </div>
              <div>
                <span className="font-bold text-gray-800 text-sm">📍 Bodega:</span>
                <p className="text-base text-gray-700 ml-4">{alarmData.bodega}</p>
              </div>
              <div>
                <span className="font-bold text-gray-800 text-sm">📋 Descripción:</span>
                <p className="text-sm text-gray-700 ml-4">{alarmData.descripcion}</p>
              </div>
              {alarmData.assignedBy && (
                <div>
                  <span className="font-bold text-gray-800 text-sm">👤 Asignado por:</span>
                  <p className="text-sm text-blue-600 ml-4 font-semibold">{alarmData.assignedBy}</p>
                </div>
              )}
              {alarmData.assignedTo && (
                <div>
                  <span className="font-bold text-gray-800 text-sm">👥 Asignado a:</span>
                  <p className="text-sm text-green-600 ml-4 font-semibold">{alarmData.assignedTo}</p>
                </div>
              )}
            </div>
          </div>

          {/* Mensaje de acción */}
          <p className="text-lg text-yellow-200 font-bold mb-4">
            ⚡ ¡Revisa el módulo TAREAS para gestionar! ⚡
          </p>

          {/* Botón para cerrar */}
          <button
            onClick={handleClose}
            className="bg-yellow-400 hover:bg-yellow-500 text-red-800 font-black text-lg px-6 py-3 rounded-lg transition-all transform hover:scale-105 shadow-lg"
          >
            ✅ ENTENDIDO
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente Principal de la Aplicación
const MainApp = () => {
  const { currentUser, setCurrentUser, db, inventoryLocalData, setSiigoProducts } = useApp();
  const [activeModule, setActiveModule] = useState('welcome');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // 🌐 INTEGRACIÓN WEBSOCKET TIEMPO REAL
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  
  // Debug: Log del currentUser para verificar estructura
  React.useEffect(() => {
    console.log('👤 MainApp - currentUser cambió:', currentUser);
    console.log('👤 MainApp - Tiene access_token:', !!currentUser?.access_token);
  }, [currentUser]);
  
  const {
    connectionStatus,
    isConnected,
    notifications,
    removeNotification,
    clearNotifications,
    showTaskAlarm: wsShowTaskAlarm,
    alarmData: wsAlarmData,
    closeTaskAlarm: wsCloseTaskAlarm,
    sendMessage
  } = useWebSocket(currentUser, backendUrl);
  
  // Sistema de alarmas simplificado - Detecta nuevas tareas para el usuario actual
  const {
    showAlarm: taskAlarmShow,
    alarmTask: taskAlarmData, 
    closeAlarm: taskAlarmClose
  } = useTaskAlarms(currentUser);
  
  // Sistema de respaldo por polling cuando WebSocket no funciona (DESACTIVADO)
  const {
    newTaskDetected,
    clearNewTask
  } = useTaskPolling(currentUser, false); // DESACTIVADO - Usando useTaskAlarms en su lugar
  
  // Estado combinado de alarmas (WebSocket + Polling)
  const [localShowTaskAlarm, setLocalShowTaskAlarm] = React.useState(false);
  const [localAlarmData, setLocalAlarmData] = React.useState(null);
  
  // Detectar nueva tarea del polling y mostrar alarma
  React.useEffect(() => {
    if (newTaskDetected && !isConnected) {
      console.log('🚨 Mostrando alarma de polling para tarea:', newTaskDetected);
      setLocalAlarmData(newTaskDetected);
      setLocalShowTaskAlarm(true);
    }
  }, [newTaskDetected, isConnected]);
  
  // Usar el nuevo sistema de alarmas simplificado
  const showTaskAlarm = taskAlarmShow;
  const alarmData = taskAlarmData;
  const closeTaskAlarm = taskAlarmClose;

  // ========================================================================
  // 🔄 SINCRONIZACIÓN AUTOMÁTICA DE INVENTARIO EN SEGUNDO PLANO
  // ========================================================================
  const [syncStatus, setSyncStatus] = React.useState({
    isSyncing: false,
    lastSync: null,
    error: null,
    productsCount: 0
  });

  const isSyncingRef = React.useRef(false);

  const sincronizarInventarioSiigo = React.useCallback(async () => {
    if (isSyncingRef.current) {
      return;
    }
    isSyncingRef.current = true;

    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      console.log('Iniciando sincronizacion automatica de inventario con Siigo...');

      // 1. Cargar productos de Siigo
      const productsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/products`);
      const productsData = await productsResponse.json();

      if (!productsData.success || !productsData.products) {
        throw new Error('No se pudieron cargar productos de Siigo');
      }

      // Alimentar el contexto compartido (siigoProducts) para que el Visualizador
      // y otros componentes abran instantaneamente sin re-descargar
      if (setSiigoProducts) {
        setSiigoProducts(productsData.products);
        try {
          sessionStorage.setItem('siigo_products_v1', JSON.stringify(productsData.products));
        } catch (_) {}
      }

      // 2. Usar datos locales precargados del contexto, o cargar desde MongoDB como fallback
      let datosLocales = inventoryLocalData || {};
      if (Object.keys(datosLocales).length === 0) {
        const localDataResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/local-data`);
        const localDataResult = await localDataResponse.json();
        datosLocales = localDataResult.success ? localDataResult.data : {};
      }

      // 3. Combinar productos de Siigo con datos locales
      const productosCompletos = productsData.products.map(p => {
        const datosProducto = datosLocales[p.id] || { costo: 0, stock_minimo: 0 };
        
        // Mapear impuestos
        let impuestoNombre = 'No aplica';
        if (p.taxes && p.taxes.length > 0) {
          const taxId = p.taxes[0].id;
          if (taxId === 5848) impuestoNombre = 'IVA 19%';
          else if (taxId === 5849) impuestoNombre = 'IVA 5%';
          else if (taxId === 13939) impuestoNombre = 'IVA 0%';
          else impuestoNombre = p.taxes[0].name || 'No aplica';
        }

        // Encontrar precios
        const priceList = p.prices?.[0]?.price_list || [];
        const efectivo = priceList.find(pl => pl.position === 1)?.value || 0;
        const tarjeta = priceList.find(pl => pl.position === 2)?.value || 0;

        return {
          id: p.id,
          codigo: p.code || '',
          nombre: p.name || '',
          categoria: p.account_group?.name || 'Sin categoría',
          categoriaId: p.account_group?.id || null,
          cantidad: p.available_quantity || 0,
          stock_minimo: datosProducto.stock_minimo || 0,
          costo: datosProducto.costo || 0,
          precio_venta: efectivo || tarjeta || 0,
          precio_lista1: efectivo,
          precio_lista2: tarjeta,
          referencia: p.reference || '',
          producto_inventariable: p.stock_control !== false,
          impuesto_cargo: impuestoNombre,
          incluir_iva_precio: p.tax_included || false,
          active: p.active !== false,
          warehouses: p.warehouses || []  // Stock por bodega desde Siigo
        };
      });

      // 4. Guardar en localStorage para acceso rápido desde el módulo Inventario.
      // Wrapped en try/catch porque el cache puede exceder la cuota (~5MB) en algunos navegadores.
      // Si falla, NO interrumpe la sincronización; los productos ya estan en el contexto compartido.
      try {
        localStorage.setItem('siigo_productos_cache', JSON.stringify(productosCompletos));
        localStorage.setItem('siigo_sync_timestamp', new Date().toISOString());
      } catch (storageError) {
        console.warn('⚠️ No se pudo guardar siigo_productos_cache en localStorage (cuota excedida). Se usa solo memoria.');
        // Limpiar entradas viejas para liberar espacio en proximos syncs
        try {
          localStorage.removeItem('siigo_productos_cache');
          localStorage.removeItem('siigo_sync_timestamp');
        } catch (_) {}
      }

      setSyncStatus({
        isSyncing: false,
        lastSync: new Date().toISOString(),
        error: null,
        productsCount: productosCompletos.length
      });

      console.log(`✅ Sincronización completada: ${productosCompletos.length} productos actualizados`);
    } catch (error) {
      console.error('❌ Error en sincronización automática:', error);
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.message
      }));
    } finally {
      isSyncingRef.current = false;
    }
  }, [inventoryLocalData, setSiigoProducts]);

  // Ref para mantener la ultima version de la funcion de sync sin triggear el efecto
  const syncFnRef = React.useRef(sincronizarInventarioSiigo);
  React.useEffect(() => {
    syncFnRef.current = sincronizarInventarioSiigo;
  }, [sincronizarInventarioSiigo]);

  // Ejecutar sincronización al iniciar sesión y periódicamente cada 5 minutos.
  // Solo depende de currentUser para evitar reinicios del intervalo.
  React.useEffect(() => {
    if (!currentUser) return;

    console.log('🚀 Iniciando sistema de sincronización automática de inventario');

    // Sincronización inicial inmediata
    syncFnRef.current && syncFnRef.current();

    // Configurar sincronización periódica cada 5 minutos (300000 ms)
    const syncInterval = setInterval(() => {
      console.log('⏰ Ejecutando sincronización programada...');
      syncFnRef.current && syncFnRef.current();
    }, 300000); // 5 minutos

    // Limpiar intervalo al desmontar o cerrar sesión
    return () => {
      console.log('🛑 Deteniendo sistema de sincronización automática');
      clearInterval(syncInterval);
    };
  }, [currentUser]);

  const handleLogout = () => {
    // Limpiar localStorage y sesión
    authService.logout();
    setCurrentUser(null);
    setActiveModule('welcome');
    console.log('✅ Sesión cerrada correctamente');
  };

  const hasPermission = (module) => {
    if (!currentUser) return false;
    
    // El usuario puede estar en formato de backend (con .user) o formato simple
    const user = currentUser.user || currentUser;
    
    // Solo administradores tienen acceso completo automático
    if (user.username === 'admin' || user.username === 'Leandro') {
      return true;
    }
    
    // TODOS LOS DEMÁS usuarios solo tienen acceso a módulos específicamente asignados
    const userPermissions = user.customPermissions || db.permissions[user.username] || [];
    return userPermissions.includes(module);
  };

  // Definir todos los módulos disponibles
  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <TrendingUp size={20} />, color: 'text-purple-600' },
    { id: 'accounting', label: 'Contabilidad', icon: <DollarSign size={20} />, color: 'text-green-600' },
    { id: 'finanzas', label: 'Finanzas', icon: <Upload size={20} />, color: 'text-indigo-600' },
    { id: 'cotizador', label: 'Cotizador automatico', icon: <BarChart3 size={20} />, color: 'text-indigo-600' },
    { id: 'inventario', label: 'Inventario', icon: <Package size={20} />, color: 'text-purple-600' },
    { id: 'product-visualizer', label: 'Visualizador de Productos', icon: <Eye size={20} />, color: 'text-teal-600' },
    { id: 'facturacion', label: 'Facturación', icon: <FileText size={20} />, color: 'text-blue-600' },
    { id: 'mis-facturas', label: 'Mis Facturas', icon: <FileText size={20} />, color: 'text-emerald-600' },
    { id: 'bosko', label: 'BOSKO', icon: <Package size={20} />, color: 'text-green-600' },
    { id: 'lumina', label: 'LUMINA', icon: <Layers size={20} />, color: 'text-gray-600' },
    { id: 'orders', label: 'Panel de Control', icon: <FileText size={20} />, color: 'text-blue-600' },
    { id: 'pending-orders', label: 'Pedidos Pendientes', icon: <AlertCircle size={20} />, color: 'text-orange-600' },
    { id: 'gestion-pendientes', label: 'Gestión de Pendientes', icon: <Activity size={20} />, color: 'text-cyan-600' },
    { id: 'task-scheduler', label: 'Programador de Tareas', icon: <Calendar size={20} />, color: 'text-indigo-600' },
    { id: 'calendario-bosko', label: 'Calendario Bosko', icon: <Calendar size={20} />, color: 'text-blue-600' },
    { id: 'tasks', label: 'Tareas', icon: <CheckCircle size={20} />, color: 'text-orange-600' },
    { id: 'settings', label: 'Configuración', icon: <Settings size={20} />, color: 'text-gray-600' }
  ];

  // Filtrar módulos según permisos del usuario
  const menuItems = allMenuItems.filter(item => {
    // Admins ven todo
    if (currentUser?.role === 'admin') return true;
    
    // Si tiene permisos personalizados definidos, usarlos
    const permissions = currentUser?.customPermissions || [];
    if (permissions.length > 0) {
      return permissions.includes(item.id);
    }
    
    // Si no tiene permisos definidos, mostrar todos los módulos
    return true;
  });

  // Redirigir al primer módulo permitido si no tiene acceso al módulo actual
  React.useEffect(() => {
    if (currentUser && menuItems.length > 0) {
      // No redirigir si está en la pantalla de bienvenida
      if (activeModule === 'welcome') return;
      
      // Verificar si el usuario tiene acceso al módulo actual
      const hasAccessToCurrentModule = menuItems.some(item => item.id === activeModule);
      
      if (!hasAccessToCurrentModule) {
        // Redirigir a la pantalla de bienvenida
        console.log('⚠️ Usuario no tiene acceso a', activeModule, '- Redirigiendo a bienvenida');
        setActiveModule('welcome');
      }
    }
  }, [currentUser, activeModule, menuItems]);

  const renderModule = () => {
    // La pantalla de bienvenida siempre es accesible
    if (activeModule === 'welcome') {
      return (
        <div className="flex items-center justify-center h-full bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <div className="text-center px-8">
            <h1 className="text-7xl font-black text-gray-800 mb-4 tracking-tight">
              CLENIP GROUP
            </h1>
            <p className="text-3xl text-gray-600 font-light">
              Sistema de Gestión Administrativa
            </p>
          </div>
        </div>
      );
    }
    
    // Verificar si el usuario tiene acceso al módulo actual
    const hasAccess = currentUser?.role === 'admin' || 
                      menuItems.some(item => item.id === activeModule);
    
    // Si no tiene acceso, mostrar mensaje de acceso restringido
    if (!hasAccess) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-50">
          <div className="text-center bg-white p-12 rounded-xl shadow-lg max-w-md">
            <div className="mb-6">
              <AlertCircle size={80} className="mx-auto text-red-500" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-3">Acceso Restringido</h2>
            <p className="text-gray-600 mb-6">
              No tienes permisos para acceder al módulo <strong>{activeModule}</strong>.
            </p>
            {menuItems.length > 0 && (
              <button
                onClick={() => setActiveModule(menuItems[0].id)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                Ir a {menuItems[0].label}
              </button>
            )}
          </div>
        </div>
      );
    }
    
    // Renderizar el módulo correspondiente
    switch(activeModule) {
      case 'dashboard':
        return <Dashboard />;
      case 'accounting':
        return <Accounting />;
      case 'finanzas':
        return <Finanzas />;
      case 'cotizador':
        return <CotizadorAutomatico />;
      case 'inventario':
        return <Inventario />;
      case 'facturacion':
        return <Facturacion />;
      case 'mis-facturas':
        return <MisFacturas />;
      case 'bosko':
        return <Bosko />;
      case 'lumina':
        return <LuminaModule />;
      case 'orders':
        return <ControlPanel />;
      case 'pending-orders':
        return <PendingOrders />;
      case 'gestion-pendientes':
        return <GestionPendientes />;
      case 'task-scheduler':
        return <TaskScheduler />;
      case 'tasks':
        return <TasksModule />;
      case 'settings':
        return <SettingsModule />;
      case 'product-visualizer':
        return <ProductVisualizer />;
      case 'calendario-bosko':
        return <CalendarioBosko />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <h1 className="text-xl font-bold text-purple-600">CLENIP GROUP</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden"
          >
            <X size={24} />
          </button>
        </div>
        
        <nav className="p-4 flex-1 overflow-y-auto">
          {menuItems.map(item => (
            hasPermission(item.id) && (
              <button
                key={item.id}
                onClick={() => {
                  setActiveModule(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 mb-1 ${
                  activeModule === item.id
                    ? 'bg-purple-100 text-purple-700'
                    : 'hover:bg-gray-100'
                }`}
              >
                <span className={item.color}>{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </button>
            )
          ))}
        </nav>

        <div className="flex-shrink-0 w-full p-4 border-t bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users size={20} className="text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{(currentUser.user || currentUser).name || currentUser.username}</p>
                <p className="text-xs text-gray-500 capitalize truncate">{(currentUser.user || currentUser).role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-500 hover:text-red-500 transition-colors flex-shrink-0"
              title="Cerrar sesión"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu size={24} />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              {menuItems.find(item => item.id === activeModule)?.label || 'Dashboard'}
            </h2>
            
            {/* Indicador de Sincronización de Inventario */}
            <div className="flex items-center gap-4">
              {syncStatus.lastSync && (
                <div className="flex items-center gap-2 text-sm">
                  {syncStatus.isSyncing ? (
                    <>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-blue-600 font-medium">Sincronizando inventario...</span>
                    </>
                  ) : syncStatus.error ? (
                    <>
                      <div className="w-2 h-2 bg-red-500 rounded-full shadow-sm"></div>
                      <span className="text-red-600 text-xs" title={syncStatus.error}>Error en sincronización</span>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 bg-green-500 rounded-full shadow-sm"></div>
                      <span className="text-gray-600 text-xs">
                        {syncStatus.productsCount} productos • 
                        Última sync: {new Date(syncStatus.lastSync).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </>
                  )}
                </div>
              )}
              
              <div className="text-sm text-gray-600">
                {new Date().toLocaleDateString('es-CO', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">
          {renderModule()}
        </main>
      </div>

      {/* 🌐 NOTIFICACIONES EN TIEMPO REAL */}
      <RealTimeNotifications 
        notifications={notifications}
        removeNotification={removeNotification}
        clearNotifications={clearNotifications}
        connectionStatus={connectionStatus}
      />
      
      {/* 🚨 ALARMA WEBSOCKET PARA NUEVAS TAREAS */}
      <TaskAlarmWebSocket 
        showTaskAlarm={showTaskAlarm}
        alarmData={alarmData}
        closeTaskAlarm={closeTaskAlarm}
      />
    </div>
  );
};

// Pantalla de carga optimizada
const LoadingScreen = ({ progress }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
    <div className="text-center">
      <h1 className="text-4xl font-black text-white mb-3 tracking-tight">CLENIP GROUP</h1>
      <p className="text-blue-300 mb-8 text-sm">Sistema de Gestion Empresarial</p>
      
      <div className="w-72 mx-auto">
        <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden mb-3">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress.percent}%` }}
          />
        </div>
        <p className="text-slate-400 text-xs">{progress.step}</p>
      </div>
    </div>
  </div>
);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </QueryClientProvider>
  );
}

function AppContent() {
  const { currentUser, isLoading, loadingProgress } = useApp();
  
  if (isLoading && currentUser) {
    return <LoadingScreen progress={loadingProgress} />;
  }
  
  return currentUser ? <MainApp /> : <Login />;
}